import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Box,
    CircularProgress,
    TextField,
} from '@mui/material';
import { useNotify, usePermissions } from 'react-admin';
import { getAllTimeoffRequests, TimeoffRequest } from '../dataProvider';
import { TimeoffRequestDetailDialog } from './TimeoffRequestDetailDialog';

/** Page component displaying all company-wide time-off requests with client-side filters. */
export const AllRequestsList: React.FC = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const { permissions } = usePermissions<string[]>();
    const [requests, setRequests] = useState<TimeoffRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [nameFilter, setNameFilter] = useState('');
    const [yearFilter, setYearFilter] = useState('');
    const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);

    const canViewAll =
        Array.isArray(permissions) && permissions.includes('timeoff.view_all_requests');

    useEffect(() => {
        if (!canViewAll) return;
        (async () => {
            setLoading(true);
            try {
                const data = await getAllTimeoffRequests();
                setRequests(data);
            } catch (error: any) {
                const errorMessage =
                    error?.body?.message || error?.message || 'Failed to load requests';
                notify(errorMessage, { type: 'error' });
            } finally {
                setLoading(false);
            }
        })();
    }, [canViewAll, notify]);

    if (!canViewAll) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Typography>You do not have permission to view all requests.</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    const filtered = requests.filter((r) => {
        const nameMatch = nameFilter
            ? (r.full_name ?? '').toLowerCase().includes(nameFilter.toLowerCase())
            : true;
        const yearMatch = yearFilter
            ? new Date(r.start_date).getFullYear().toString() === yearFilter
            : true;
        return nameMatch && yearMatch;
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Approved': return 'success';
            case 'Rejected': return 'error';
            case 'Cancelled': return 'default';
            case 'Pending': return 'warning';
            default: return 'default';
        }
    };

    const handleRowClick = (requestId: number) => {
        setSelectedRequestId(requestId);
        setDetailOpen(true);
    };

    return (
        <Card>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">All Time-Off Requests</Typography>
                    <Button variant="outlined" onClick={() => navigate('/timeoff')}>
                        Back
                    </Button>
                </Box>

                <Box display="flex" gap={2} mb={2}>
                    <TextField
                        label="Filter by employee name"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        size="small"
                        inputProps={{ 'aria-label': 'filter by employee name' }}
                    />
                    <TextField
                        label="Filter by year"
                        value={yearFilter}
                        onChange={(e) => setYearFilter(e.target.value)}
                        size="small"
                        placeholder="e.g. 2024"
                        inputProps={{ 'aria-label': 'filter by year' }}
                    />
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Employee</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Leave Type</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Work Days</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No requests found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filtered.map((request) => (
                                    <TableRow
                                        key={request.id}
                                        hover
                                        onClick={() => handleRowClick(request.id)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>{request.full_name ?? ''}</TableCell>
                                        <TableCell>{request.email ?? ''}</TableCell>
                                        <TableCell>{request.leave_type}</TableCell>
                                        <TableCell>
                                            {new Date(request.start_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(request.end_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                                        </TableCell>
                                        <TableCell>{request.work_days}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={request.status}
                                                color={getStatusColor(request.status) as any}
                                                size="small"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>

            <TimeoffRequestDetailDialog
                open={detailOpen}
                requestId={selectedRequestId}
                onClose={() => setDetailOpen(false)}
            />
        </Card>
    );
};
